import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { mapLegStatus } from '../../lib/bet-status';
import { removePipes } from '../../lib';

import {
  BetLegsBlockHead,
  BetLegsScrollContainer,
  IProps,
  Leg,
  renderCompetitionSportCell,
  renderEventNameCell,
  renderHeaderRow,
  renderMarketCell,
  renderOddsCell,
  renderResultCellIcon,
  renderSelectionsCell
} from './bet-legs';
import AccordionSGP from './accordion-sgp';
import { IBetLeg, ILegResult } from '../../types';
import { Block } from '../typography';
import { inferSGPResult, sportsIconsByResultType } from './';

export interface ISuperParlayProps extends IProps {
  spBetLegs: IBetLeg[];
}

const renderSuperParlayLegs = ({
  getLocalDateTime,
  isCompetitionSportVisible,
  isEventStartTimeVisible,
  legs,
  spBetLegs,
  paddingHorizontal,
  type,
  gridTemplateColumns,
  variant
}: Required<
  Pick<
    ISuperParlayProps,
    | 'getLocalDateTime'
    | 'isCompetitionSportVisible'
    | 'isEventStartTimeVisible'
    | 'legs'
    | 'spBetLegs'
    | 'paddingHorizontal'
    | 'type'
    | 'variant'
  >
> &
  Pick<ISuperParlayProps, 'gridTemplateColumns'>) => {
  return spBetLegs.map((spBetLeg: IBetLeg, i: number) => {
    return (
      <Fragment key={`${spBetLeg.type}-${i}`}>
        <AccordionSGP
          isSGP={isSGPBetLeg()}
          legDesc={getLegDescription(spBetLeg)}
          eventName={getFormattedEventName()}
          competitionSportName={getCompetitionSportName(isCompetitionSportVisible)}
          sgpOdds={spBetLeg.price?.a ?? ''}
          sgpOriginalOdds={spBetLeg.originalPrice?.a ?? ''}
          sgpResult={inferSGPResult(legs, spBetLeg)}
          gridTemplateColumns={gridTemplateColumns}
          paddingHorizontal={paddingHorizontal}
          isCompetitionSportVisible={isCompetitionSportVisible}>
          {spBetLeg.legRefs.map((legRef: string) => {
            const leg: Leg | undefined = legs.find((leg: Leg) => leg.id === legRef);

            if (!leg) return null;

            const { selection, market, event, price, eventStartTime, competition, sport } = leg;
            const result: Pick<ILegResult, 'type'> | null | undefined = leg.result;
            const { label } = mapLegStatus(result);
            const name = removePipes(selection.name);
            const isVoided = result?.type === 'void';
            const isInjuryProtected = result?.type === 'injury_protection';
            const isStruckThrough = isVoided || isInjuryProtected;

            return (
              <BetLegsBlockRow
                {...{ gridTemplateColumns, paddingHorizontal }}
                isSGPBetLeg={isSGPBetLeg()}
                size={isSGPBetLeg() ? 'sm' : 'lg'}
                weight={100}
                type={result?.type}
                className={`qa-bet-finder-leg qa-bet-finder-leg-${leg.id}`}
                key={`bet-finder-leg-${leg.id}`}>
                <div className="qa-bet-finder-cell">{!isSGPBetLeg() && getLegDescription(spBetLeg)}</div>
                {renderSelectionsCell({ name, leg, isStruckThrough })}
                {renderMarketCell({ market, isStruckThrough })}
                <div>
                  {!isSGPBetLeg() &&
                    renderEventNameCell({
                      event,
                      isEventStartTimeVisible,
                      getLocalDateTime,
                      eventStartTime,
                      isStruckThrough
                    })}
                </div>
                {isCompetitionSportVisible && (
                  <div>{!isSGPBetLeg() && renderCompetitionSportCell({ competition, sport, isStruckThrough })}</div>
                )}
                <div>{!isSGPBetLeg() && renderOddsCell({ type, price, isVoided: isStruckThrough })}</div>
                {renderResultCellIcon({
                  label,
                  icon: sportsIconsByResultType[result?.type || ''],
                  isInjuryProtected,
                  isSGPBetLeg: isSGPBetLeg(),
                  variant
                })}
              </BetLegsBlockRow>
            );
          })}
        </AccordionSGP>
      </Fragment>
    );

    function getFormattedEventName() {
      const leg: Leg = getFirstLeg();
      const { event, eventStartTime } = leg;
      event.name = removePipes(event.name);

      return renderEventNameCell({
        event,
        isEventStartTimeVisible,
        getLocalDateTime,
        eventStartTime,
        isStruckThrough: false
      });
    }

    function getCompetitionSportName(isCompetitionSportVisible: boolean) {
      const leg: Leg = getFirstLeg();
      return (
        isCompetitionSportVisible &&
        renderCompetitionSportCell({ competition: leg.competition, sport: leg.sport, isStruckThrough: false })
      );
    }

    function isSGPBetLeg(): boolean {
      return spBetLeg.type === 'leg-group';
    }

    function getFirstLeg() {
      const legRef = spBetLeg.legRefs[0];
      const leg: Leg = legs.find(leg => leg.id === legRef) as Leg;
      return leg;
    }

    function getLegDescription(spBetLeg: IBetLeg) {
      if (spBetLeg.type === 'leg-group') {
        return spBetLeg.legRefs.length + ' Leg SGP';
      } else if (spBetLeg.type === 'standard') {
        return 'Straight';
      } else {
        return '';
      }
    }
  });
};

export default function SuperParlayBetLegs({
  getLocalDateTime,
  gridTemplateColumns,
  isCompetitionSportVisible = true,
  isEventStartTimeVisible = true,
  legs,
  paddingHorizontal = '2rem',
  type,
  spBetLegs,
  variant
}: ISuperParlayProps) {
  return (
    <BetLegsScrollContainer>
      <BetLegsHeader className="qa-bet-finder-bet-legs-header" size="lg" {...{ gridTemplateColumns }}>
        <BetLegsBlockHead className="qa-bet-finder-header" {...{ paddingHorizontal }}>
          <div className="qa-bet-finder-cell">
            <span>Leg</span>
          </div>
          {renderHeaderRow(legs, isCompetitionSportVisible)}
        </BetLegsBlockHead>
      </BetLegsHeader>
      <BetLegsBlock className="qa-bet-finder-bet-legs-table" size="lg">
        {renderSuperParlayLegs({
          getLocalDateTime,
          isCompetitionSportVisible,
          isEventStartTimeVisible,
          legs,
          spBetLegs,
          paddingHorizontal,
          type,
          gridTemplateColumns,
          variant
        })}
      </BetLegsBlock>
    </BetLegsScrollContainer>
  );
}

export const SP_GRID_TEMPLATE_COLUMNS = '11fr 11fr 17fr 28fr 17fr 6fr 10fr';

const BetLegsHeader: any = styled(Block)<{ gridTemplateColumns: string; height: string }>`
  background-color: ${({ theme }) => theme.colors.white};
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns || SP_GRID_TEMPLATE_COLUMNS};
  text-align: left;
`;

const BetLegsBlock: any = styled(Block)`
  background-color: ${({ theme }) => theme.colors.white};
  display: block;
  text-align: left;
`;

const BetLegsBlockRow = styled(Block)<{
  gridTemplateColumns: string | undefined;
  paddingHorizontal: string;
  isSGPBetLeg: boolean;
  type?: string;
}>`
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns || SP_GRID_TEMPLATE_COLUMNS};
  & > div {
    padding: 0.6rem 0;
    ${({ theme, type }) =>
      (type === 'void' || type === 'injury_protection') &&
      css`
        background-color: ${type === 'void' ? theme.colors.status.alert : theme.colors.status.injuryProtection};
      `}
  }

  & > div:first-child {
    padding-left: ${({ paddingHorizontal }) => paddingHorizontal};
  }
  & > div:last-child {
    display: flex;
    justify-content: flex-end;
    padding-right: ${({ paddingHorizontal }) => paddingHorizontal};
    ${({ isSGPBetLeg }) =>
      isSGPBetLeg &&
      `
    padding-left: 0.35rem;
    `}
  }
`;
